import { DocumentNode, OperationDefinitionNode } from 'graphql';

/**
 * How many results are kept. Entries are keyed by what the query actually depends on, so
 * selections built around the same product share one entry and the limit counts distinct
 * product configurations rather than rows on the page.
 */
const LIMIT = 100;

// Insertion order is the recency order: the first key is the least recently used one.
const store = new Map<string, unknown>();

const operationName = (query: DocumentNode): string => {
    const operation = query.definitions.find(
        (definition): definition is OperationDefinitionNode => definition.kind === 'OperationDefinition',
    );
    return operation?.name?.value ?? 'anonymous';
};

/**
 * Identifies a result by the query and the variables it was fetched with — which for these
 * queries means the product, the region and the locale. Two selections of the same product
 * therefore land on the same key.
 */
export const selectionDataKey = (query: DocumentNode, variables: unknown): string => (
    `${operationName(query)}(${JSON.stringify(variables ?? null)})`
);

/**
 * The stored result, or undefined when nothing is cached. A plain lookup: it does not count as
 * a use, so it is safe to call while rendering — see `touchSelectionData`.
 */
export const readSelectionData = <TData>(key: string): TData | undefined => (
    // Safe because a key embeds the query the value was fetched for.
    store.get(key) as TData | undefined
);

/** Marks a cached result as the most recently used one, so the eviction skips it. */
export const touchSelectionData = (key: string) => {
    if (!store.has(key)) {
        return;
    }
    const value = store.get(key);
    store.delete(key);
    store.set(key, value);
};

export const writeSelectionData = (key: string, value: unknown) => {
    store.delete(key);
    store.set(key, value);

    while (store.size > LIMIT) {
        const oldest = store.keys().next();
        if (oldest.done === true) {
            return;
        }
        store.delete(oldest.value);
    }
};

/** Test seam: the cache lives for as long as the page does. */
export const clearSelectionDataCache = () => {
    store.clear();
};
